import { Component } from "react";
import { connect } from "react-redux";
import moment from "moment";

import { Get, Post, Patch } from "utils/axios";
import { requestError, requestSuccess } from "utils/requestHandler";
import { addLoadingContent, removeLoadingContent } from "reducers/LoadingOverlay";
import { convertObjectToBase64 } from "utils/objToBase64";

const DEFAULT_TAX_MEMBERS = [
  { member_id: "MY15117326", fullname: "ROSIDAH BINTI SANTAN" },
  { member_id: "MY31873028", fullname: "SHELLAH JACOB" },
  { member_id: "MY39432259", fullname: "INTAN SHAKILA BINTI MISRAH" },
];

const cloneDefaultTaxMembers = () => DEFAULT_TAX_MEMBERS.map((member) => ({ ...member }));

const HOC = (WrappedComponent) => {
  class WithHOC extends Component {
    state = {
      // Trigger panel
      triggerMonth: moment().subtract(1, "months").format("YYYY-MM"),
      selectedPrograms: [],
      triggerProgramOptions: [],
      triggerResult: null,
      triggerLoading: false,

      // Queue list
      filterQuery: {
        month: null,
        transfer_type: null,
        is_ready: null,
        is_back_order: null,
        status: null,
        program_id: null,
        member_id: null,
        from_member_id: null,
        remarks: null,
        page: 1,
        pageSize: 100,
      },
      entries: [],
      totalCount: 0,
      notReadyCount: 0,
      totalAmount: 0,
      availableMonthOptions: [],
      listMeta: {
        isPaginated: true,
        page: 1,
        pageSize: 100,
        totalPages: 0,
      },
      filterProgramOptions: [],
      selectedIds: [],
      listLoading: false,

      // Bulk actions
      deleteModalVisible: false,
      deleteReason: "",
      deleteAllModalVisible: false,
      deleteAllReason: "",
      bulkActionLoading: false,

      // Tax deduction
      taxMonth: moment().subtract(1, "months").format("YYYY-MM"),
      memberSearchQuery: "",
      memberSearchResults: [],
      memberSearchLoading: false,
      selectedMembers: cloneDefaultTaxMembers(),
      taxResults: null,
      taxLoading: false,
    };

    load = (param) => {
      if (param) {
        this.props.addLoadingContent();
      } else {
        this.props.removeLoadingContent();
      }
    };

    onChangeHOC = (key, val) => this.setState({ [key]: val });

    componentDidMount() {
      this.getProgramOptions();
    }

    mapProgramOptions = (programs = []) =>
      programs.map((program) => ({
        id: program.id,
        value: program.id,
        label: program.name,
      }));

    getActivePrograms = (onSuccess) => {
      Get(
        "/wallet_injection_queue/active-programs",
        (resp) => {
          const programs = resp.data?.programs || [];
          onSuccess(this.mapProgramOptions(programs));
        },
        (err) => {
          requestError(err);
        },
        () => {}
      );
    };

    getProgramOptions = () => {
      this.getActivePrograms((programOptions) => {
        this.setState((prevState) => {
          const allowedProgramIds = new Set(programOptions.map((option) => option.value));
          return {
            triggerProgramOptions: programOptions,
            filterProgramOptions: programOptions,
            selectedPrograms: prevState.selectedPrograms.filter((programId) => allowedProgramIds.has(programId)),
            filterQuery:
              prevState.filterQuery.program_id && !allowedProgramIds.has(prevState.filterQuery.program_id)
                ? {
              ...prevState.filterQuery,
              program_id: null,
              page: 1,
                }
                : prevState.filterQuery,
          };
        });
      });
    };

    // ─── Trigger / Sync ────────────────────────────────────────────────────────

    triggerSync = () => {
      const { triggerMonth, selectedPrograms } = this.state;
      const payload = {
        month: triggerMonth || undefined,
      };
      if (selectedPrograms.length > 0) {
        payload.program_ids = selectedPrograms;
      }
      this.setState({ triggerLoading: true, triggerResult: null });
      Post(
        "/wallet_injection_queue/trigger",
        payload,
        this.triggerSyncSuccess,
        (err) => {
          this.setState({ triggerLoading: false });
          requestError(err);
        },
        () => {}
      );
    };

    triggerSyncSuccess = (resp) => {
      const triggeredMonth = resp.data?.month; // "YYYY-MM" or null
      const { filterQuery } = this.state;
      const newFilterQuery = triggeredMonth
        ? { ...filterQuery, month: triggeredMonth.replace("-", ""), page: 1 }
        : filterQuery;
      this.setState({ triggerLoading: false, triggerResult: resp.data, filterQuery: newFilterQuery });
      requestSuccess("Sync triggered successfully");
      if (newFilterQuery === filterQuery) {
        this.getQueueList();
      }
    };

    // ─── Queue List ────────────────────────────────────────────────────────────

    getQueueList = () => {
      const { filterQuery } = this.state;
      const query = {
        ...(filterQuery.month && { month: filterQuery.month }),
        ...(filterQuery.is_ready !== null && filterQuery.is_ready !== undefined && { is_ready: filterQuery.is_ready }),
        ...(filterQuery.is_back_order !== null && filterQuery.is_back_order !== undefined && { is_back_order: filterQuery.is_back_order }),
        ...(filterQuery.status && { status: filterQuery.status }),
        ...(filterQuery.transfer_type && { transfer_type: filterQuery.transfer_type }),
        ...(filterQuery.program_id && { program_id: filterQuery.program_id }),
        ...(filterQuery.member_id && { member_id: filterQuery.member_id }),
        ...(filterQuery.from_member_id && { from_member_id: filterQuery.from_member_id }),
        ...(filterQuery.remarks && { remarks: filterQuery.remarks }),
        limit: filterQuery.pageSize,
        offset: (filterQuery.page - 1) * filterQuery.pageSize,
      };

      this.setState({ listLoading: true, selectedIds: [] });
      Post("/wallet_injection_queue/process", {}, () => {}, () => {}, () => {});
      Get(
        `/wallet_injection_queue/list?query=${convertObjectToBase64(query)}`,
        this.getQueueListSuccess,
        (err) => {
          this.setState({ listLoading: false });
          requestError(err);
        },
        () => {}
      );
    };

    getQueueListSuccess = (resp) => {
      const data = resp.data;
      const { filterQuery } = this.state;
      const nextFilterQuery =
        filterQuery.month === null && data.month
          ? { ...filterQuery, month: data.month }
          : filterQuery;
      const totalCount = data.total_count || 0;
      const notReadyCount = data.not_ready_count ?? 0;
      const totalAmount = data.total_amount ?? 0;
      const totalPages = Math.max(1, Math.ceil(totalCount / filterQuery.pageSize));
      this.setState({
        listLoading: false,
        entries: data.entries || [],
        totalCount,
        notReadyCount,
        totalAmount,
        availableMonthOptions: (data.available_months || []).map((m) => ({
          value: m,
          label: `${m.slice(0, 4)}-${m.slice(4)}`,
        })),
        listMeta: {
          isPaginated: true,
          page: filterQuery.page,
          pageSize: filterQuery.pageSize,
          totalPages,
        },
        filterQuery: nextFilterQuery,
      });
    };

    // ─── Bulk: Mark as Ready ───────────────────────────────────────────────────

    markAsReady = (ids) => {
      const { filterQuery } = this.state;
      const payload = { ids, is_ready: true };
      if (filterQuery.month) payload.month = filterQuery.month;
      if (filterQuery.transfer_type) payload.transfer_type = filterQuery.transfer_type;
      if (filterQuery.program_id) payload.program_id = filterQuery.program_id;
      if (filterQuery.member_id) payload.member_id = filterQuery.member_id;
      if (filterQuery.from_member_id) payload.from_member_id = filterQuery.from_member_id;
      if (filterQuery.remarks) payload.remarks = filterQuery.remarks;

      this.setState({ bulkActionLoading: true });
      Patch(
        "/wallet_injection_queue/update-ready",
        payload,
        this.markAsReadySuccess,
        (err) => {
          this.setState({ bulkActionLoading: false });
          requestError(err);
        },
        () => {}
      );
    };

    markAsReadySuccess = (resp) => {
      const { updated_count } = resp.data;
      this.setState({ bulkActionLoading: false, selectedIds: [] });
      requestSuccess(`${updated_count} item(s) marked as ready — processing started`);
      setTimeout(() => this.getQueueList(), 3000);
    };

    // ─── Bulk: Tag as Back Order ───────────────────────────────────────────────

    tagBackOrder = (ids, isBackOrder) => {
      this.setState({ bulkActionLoading: true });
      Patch(
        "/wallet_injection_queue/update-back-order",
        { ids, is_back_order: isBackOrder },
        this.tagBackOrderSuccess,
        (err) => {
          this.setState({ bulkActionLoading: false });
          requestError(err);
        },
        () => {}
      );
    };

    tagBackOrderSuccess = (resp) => {
      const { updated_count, skipped_ids, skipped_reason, is_back_order } = resp.data;
      this.setState({ bulkActionLoading: false, selectedIds: [] });
      requestSuccess(
        `${updated_count} item(s) ${is_back_order ? "tagged as back order" : "untagged"}`
      );
      if (skipped_ids && skipped_ids.length > 0) {
        requestError(`${skipped_ids.length} row(s) skipped — ${skipped_reason}`);
      }
      this.getQueueList();
    };

    // ─── Bulk: Tag All as Back Order ───────────────────────────────────────────

    tagAllBackOrder = (isBackOrder) => {
      const { filterQuery } = this.state;
      const payload = { is_back_order: isBackOrder, month: filterQuery.month };
      if (filterQuery.program_id) payload.program_id = filterQuery.program_id;
      if (filterQuery.member_id) payload.member_id = filterQuery.member_id;

      this.setState({ bulkActionLoading: true });
      Patch(
        "/wallet_injection_queue/mark-all-back-order",
        payload,
        this.tagAllBackOrderSuccess,
        (err) => {
          this.setState({ bulkActionLoading: false });
          requestError(err);
        },
        () => {}
      );
    };

    tagAllBackOrderSuccess = (resp) => {
      const { updated_count, is_back_order } = resp.data;
      this.setState({ bulkActionLoading: false, selectedIds: [] });
      requestSuccess(
        `${updated_count} item(s) ${is_back_order ? "tagged as back order" : "untagged"}`
      );
      this.getQueueList();
    };

    // ─── Bulk: Mark All as Ready ───────────────────────────────────────────────

    markAllAsReady = () => {
      const { filterQuery } = this.state;
      const payload = {};
      if (filterQuery.month) payload.month = filterQuery.month;
      if (filterQuery.is_ready !== null && filterQuery.is_ready !== undefined) payload.is_ready = filterQuery.is_ready;
      if (filterQuery.status) payload.status = filterQuery.status;
      if (filterQuery.transfer_type) payload.transfer_type = filterQuery.transfer_type;
      if (filterQuery.program_id) payload.program_id = filterQuery.program_id;
      if (filterQuery.member_id) payload.member_id = filterQuery.member_id;
      if (filterQuery.from_member_id) payload.from_member_id = filterQuery.from_member_id;
      if (filterQuery.remarks) payload.remarks = filterQuery.remarks;

      this.setState({ bulkActionLoading: true });
      Patch(
        "/wallet_injection_queue/mark-all-ready",
        payload,
        this.markAllAsReadySuccess,
        (err) => {
          this.setState({ bulkActionLoading: false });
          requestError(err);
        },
        () => {}
      );
    };

    markAllAsReadySuccess = (resp) => {
      const { updated_count } = resp.data;
      this.setState({ bulkActionLoading: false });
      requestSuccess(`${updated_count} item(s) marked as ready — processing started`);
      setTimeout(() => this.getQueueList(), 3000);
    };

    // ─── Bulk: Soft Delete ─────────────────────────────────────────────────────

    softDelete = (ids, reason) => {
      const { filterQuery } = this.state;
      const payload = { ids, reason };
      if (filterQuery.month) payload.month = filterQuery.month;
      if (filterQuery.is_ready !== null && filterQuery.is_ready !== undefined) payload.is_ready = filterQuery.is_ready;
      if (filterQuery.status) payload.status = filterQuery.status;
      if (filterQuery.transfer_type) payload.transfer_type = filterQuery.transfer_type;
      if (filterQuery.program_id) payload.program_id = filterQuery.program_id;
      if (filterQuery.member_id) payload.member_id = filterQuery.member_id;
      if (filterQuery.from_member_id) payload.from_member_id = filterQuery.from_member_id;
      if (filterQuery.remarks) payload.remarks = filterQuery.remarks;

      this.setState({ bulkActionLoading: true });
      Patch(
        "/wallet_injection_queue/delete",
        payload,
        this.softDeleteSuccess,
        (err) => {
          this.setState({ bulkActionLoading: false });
          requestError(err);
        },
        () => {}
      );
    };

    softDeleteSuccess = (resp) => {
      const { deleted_count, skipped_ids } = resp.data;
      this.setState({
        bulkActionLoading: false,
        selectedIds: [],
        deleteModalVisible: false,
        deleteReason: "",
      });
      requestSuccess(`${deleted_count} item(s) deleted`);
      if (skipped_ids && skipped_ids.length > 0) {
        requestError(
          `${skipped_ids.length} row(s) skipped — currently processing and cannot be deleted`
        );
      }
      this.getQueueList();
    };

    // ─── Bulk: Delete All ──────────────────────────────────────────────────────

    deleteAll = (reason) => {
      const { filterQuery } = this.state;
      const payload = { reason };
      if (filterQuery.month) payload.month = filterQuery.month;
      if (filterQuery.is_ready !== null && filterQuery.is_ready !== undefined) payload.is_ready = filterQuery.is_ready;
      if (filterQuery.status) payload.status = filterQuery.status;
      if (filterQuery.transfer_type) payload.transfer_type = filterQuery.transfer_type;
      if (filterQuery.program_id) payload.program_id = filterQuery.program_id;
      if (filterQuery.member_id) payload.member_id = filterQuery.member_id;
      if (filterQuery.from_member_id) payload.from_member_id = filterQuery.from_member_id;
      if (filterQuery.remarks) payload.remarks = filterQuery.remarks;

      this.setState({ bulkActionLoading: true });
      Patch(
        "/wallet_injection_queue/delete-all",
        payload,
        this.deleteAllSuccess,
        (err) => {
          this.setState({ bulkActionLoading: false });
          requestError(err);
        },
        () => {}
      );
    };

    deleteAllSuccess = (resp) => {
      const { deleted_count } = resp.data;
      this.setState({
        bulkActionLoading: false,
        deleteAllModalVisible: false,
        deleteAllReason: "",
      });
      requestSuccess(`${deleted_count} item(s) deleted`);
      this.getQueueList();
    };

    // ─── Member Search (Tax) ───────────────────────────────────────────────────

    searchMembers = (query) => {
      if (!query || query.length < 2) {
        this.setState({ memberSearchResults: [] });
        return;
      }
      this.setState({ memberSearchLoading: true });
      Get(
        `/user_profiles/search?q=${encodeURIComponent(query)}`,
        this.searchMembersSuccess,
        (err) => {
          this.setState({ memberSearchLoading: false });
          requestError(err);
        },
        () => {}
      );
    };

    searchMembersSuccess = (resp) => {
      this.setState({
        memberSearchLoading: false,
        memberSearchResults: resp.data || [],
      });
    };

    addMember = (member) => {
      const { selectedMembers } = this.state;
      if (selectedMembers.find((m) => m.member_id === member.member_id)) return;
      this.setState({ selectedMembers: [...selectedMembers, member] });
    };

    removeMember = (member_id) => {
      this.setState({
        selectedMembers: this.state.selectedMembers.filter((m) => m.member_id !== member_id),
      });
    };

    restoreDefaultTaxMembers = () => {
      this.setState({
        selectedMembers: cloneDefaultTaxMembers(),
        memberSearchQuery: "",
        memberSearchResults: [],
      });
    };

    // ─── Tax Injection ─────────────────────────────────────────────────────────

    applyTaxDeduction = () => {
      const { taxMonth, selectedMembers } = this.state;
      const payload = {
        month: taxMonth || undefined,
        include: selectedMembers.map((m) => m.member_id),
      };
      this.setState({ taxLoading: true, taxResults: null });
      Post(
        "/wallet_injection_queue/trigger",
        payload,
        this.applyTaxDeductionSuccess,
        (err) => {
          this.setState({ taxLoading: false });
          requestError(err);
        },
        () => {}
      );
    };

    applyTaxDeductionSuccess = (resp) => {
      const results = resp.results || [];
      const alreadyQueued = results.filter((r) => r.queued === false);
      const newlyQueued = results.filter((r) => r.queued === true);
      if (newlyQueued.length > 0) {
        requestSuccess(`${newlyQueued.length} tax entry(ies) added to queue.`);
      }
      if (alreadyQueued.length > 0) {
        requestError(`${alreadyQueued.length} entry(ies) already existed in the queue and were skipped.`);
      }
      this.setState({
        taxLoading: false,
        taxResults: results,
        selectedMembers: cloneDefaultTaxMembers(),
        memberSearchQuery: "",
        memberSearchResults: [],
      });
    };

    render() {
      return (
        <WrappedComponent
          {...this.props}
          {...this.state}
          onChangeHOC={this.onChangeHOC}
          triggerSync={this.triggerSync}
          getQueueList={this.getQueueList}
          markAsReady={this.markAsReady}
          markAllAsReady={this.markAllAsReady}
          tagBackOrder={this.tagBackOrder}
          tagAllBackOrder={this.tagAllBackOrder}
          softDelete={this.softDelete}
          deleteAll={this.deleteAll}
          searchMembers={this.searchMembers}
          addMember={this.addMember}
          removeMember={this.removeMember}
          restoreDefaultTaxMembers={this.restoreDefaultTaxMembers}
          applyTaxDeduction={this.applyTaxDeduction}
          defaultTaxMembers={DEFAULT_TAX_MEMBERS}
        />
      );
    }
  }

  const mapStateToProps = (state) => ({
    data: state,
    selectedLanguage: state.ProfileReducer.profile.preferred_language,
    siteInfo: state.ClientReducer.siteInfo,
    colors: state.ClientReducer.clientInfo.styles?.colors,
  });
  return connect(mapStateToProps, { addLoadingContent, removeLoadingContent })(WithHOC);
};

export default HOC;
